import { AuthConfig, OAuthService } from 'angular-oauth2-oidc';
import { filter } from 'rxjs';
import { environment } from 'src/environments/environment';

import { UserService } from '../services/lmsuser.service';
import { TokenService } from '../interceptors/token.service';

const authConfig: AuthConfig = {
    //useSilentRefresh: true,
    //silentRefreshRedirectUri: window.location.origin + '/silent-check-sso.html',
    timeoutFactor: 0.75,

    // Url of the Identity Provider
    issuer: environment.keycloak.issuer,

    // URL of the SPA to redirect the user to after login
    redirectUri: environment.keycloak.redirectUri,

    // The SPA's id.
    // The SPA is registerd with this id at the auth-serverß
    clientId: environment.keycloak.clientId,

    responseType: environment.keycloak.responseType,
    // set the scope for the permissions the client should request
    // The first three are defined by OIDC.
    scope: environment.keycloak.scope,
    // Remove the requirement of using Https to simplify the demo
    // THIS SHOULD NOT BE USED IN PRODUCTION
    // USE A CERTIFICATE FOR YOUR IDP
    // IN PRODUCTION
    requireHttps: environment.keycloak.requireHttps,
    strictDiscoveryDocumentValidation: false,
    skipIssuerCheck: environment.keycloak.skipIssuerCheck,
    disablePKCE: false,
    oidc: false,
    clockSkewInSec: 0.75,
    checkOrigin: false,
    requestAccessToken: true,

    // at_hash is not present in JWT token
    showDebugInformation: environment.keycloak.showDebugInformation,
    disableAtHashCheck: environment.keycloak.disableAtHashCheck,
  };

// export function init_app(authInitService: AuthInitializerService): () => Promise<void> {
//   return () =>
//     authInitService.initAuth().then(() => {
//       return Promise.resolve();
//     }).catch((err)=>
//     {
//       console.error('auth init error: ',err);
//     });
// }

export function initializeAuth(oauthService: OAuthService, userService: UserService, tokenService: TokenService): () => Promise<void> {
    oauthService.configure(authConfig);
    oauthService.setStorage(localStorage);

    // Track login attempts to prevent infinite loops
    const LOGIN_RETRY_KEY = 'oauth_login_retry_count';
    const MAX_LOGIN_RETRIES = 3;

    const getRetryCount = (): number => {
      const count = sessionStorage.getItem(LOGIN_RETRY_KEY);
      return count ? parseInt(count, 10) : 0;
    };

    const incrementRetryCount = (): number => {
      const count = getRetryCount() + 1;
      sessionStorage.setItem(LOGIN_RETRY_KEY, count.toString());
      return count;
    };

    const resetRetryCount = () => {
      sessionStorage.removeItem(LOGIN_RETRY_KEY);
    };

    // Set up event handlers once - these persist across the app lifecycle
    oauthService.events
      .pipe(filter(e => e.type === 'token_error'))
      .subscribe(e => {
        console.error('OAuth token error:', e);
        // Don't automatically trigger login flow here to prevent loops
        // The 401 interceptor or user manual action will handle re-authentication
        console.warn('⚠️ Token error detected - user may need to refresh page');
      });

    oauthService.events
      .pipe(filter(e => e.type === 'invalid_nonce_in_state'))
      .subscribe(e => {
        console.error('OAuth invalid_nonce_in_state error:', e);
        console.log('🔧 Cleaning stale OAuth state and URL params before retry');
        // Clear local OAuth state only (do not trigger external logout redirect here)
        oauthService.logOut(true);
        // Remove callback params from URL to prevent stale state reprocessing
        const cleanUrl = window.location.origin + window.location.pathname;
        window.history.replaceState({}, document.title, cleanUrl);
        // Wait longer to ensure storage cleanup completes
        setTimeout(() => {
          console.log('🔐 Initiating fresh login flow after nonce error');
          oauthService.initLoginFlow();
        }, 300);
      });

    return async () => {
      // Reset logout flag to allow fresh authentication attempts
      tokenService.resetLogoutFlag();

      let profileLoaded = false; // Prevent duplicate profile loading
      let loginInitiated = false; // Prevent multiple login redirects

      try {
        const loginSuccess = await oauthService.loadDiscoveryDocumentAndTryLogin({
          onTokenReceived: (receivedToken) => {
            console.log('✅ Token received');
            resetRetryCount(); // Reset on successful token
            // Load user profile when we get a new token
            profileLoaded = true; // Mark that we're loading the profile
            userService.loadLMSUserProfile().then(() => {
              console.log('✅ LMS profile loaded');
            }).catch((err) => {
              console.warn('LMS Profile Load Error:', err.message || err);
              // Keep profileLoaded = true to prevent fallback retry loops
              // User will need to manually refresh if this fails
            });
          },
          onLoginError: (err: any) => {
            console.error(`Login Error - Code: ${err.error}, Description: ${err.error_description}`);
          }
        });

        // Check token validity
        const hasValidToken = oauthService.hasValidAccessToken() || oauthService.hasValidIdToken();

        if (!loginSuccess || !hasValidToken) {
          console.log('No valid token found after login attempt');
          // Check if we're returning from OAuth callback with an error
          const urlParams = new URLSearchParams(window.location.search);
          const hasError = urlParams.has('error');
          const hasCode = urlParams.has('code');
          const hasState = urlParams.has('state');

          if (hasError) {
            console.error('❌ OAuth callback returned error:', urlParams.get('error'), urlParams.get('error_description'));
            // Clean URL and clear local state but don't auto-retry to prevent loops
            oauthService.logOut(true);
            const cleanUrl = window.location.origin + window.location.pathname;
            window.history.replaceState({}, document.title, cleanUrl);
            return;
          }

          if (hasCode || hasState) {
            console.warn('⚠️ Callback parameters present but no valid token - possible nonce mismatch (will be handled by event)');
            return; // Let the invalid_nonce_in_state event handler deal with it
          }

          if (!hasValidToken && !loginInitiated) {
            // Check retry count to prevent infinite loops
            const retryCount = getRetryCount();

            if (retryCount >= MAX_LOGIN_RETRIES) {
              console.error('❌ Max login retry attempts exceeded. Keycloak may be unreachable.');
              console.error('Please check:');
              console.error('1. Keycloak is running and accessible');
              console.error('2. Keycloak URL in environment.ts is correct');
              console.error('3. Network connectivity to Keycloak');
              console.error(`Current issuer: ${authConfig.issuer}`);
              // alert(
              //   'Authentication service unavailable.\n\n' +
              //   'Unable to connect to the authentication server after multiple attempts.\n\n' +
              //   'Please ensure Keycloak is running and accessible, then refresh the page.\n\n' +
              //   `Keycloak URL: ${authConfig.issuer}`
              // );
              return; // Stop trying to prevent infinite loop
            }

            // No valid token and not in callback/error state - clear storage and initiate login
            loginInitiated = true;
            const newRetryCount = incrementRetryCount();
            console.log(`🔐 Clearing invalid tokens and initiating login flow... (Attempt ${newRetryCount}/${MAX_LOGIN_RETRIES})`);

            // Clear stale local tokens/state
            oauthService.logOut(true);

            // Initiate fresh login
            setTimeout(() => {
              console.log('Redirecting to Keycloak login...');
              oauthService.initLoginFlow();
            }, 100);
          }
          return;
        }

        if (hasValidToken) {
          console.log('✅ Valid access token present');

          // Only load profile if it wasn't already loaded via onTokenReceived
          if (!profileLoaded) {
            const profile = oauthService.getIdentityClaims();
            if (profile && profile['sub']) {
              profileLoaded = true; // Mark to prevent duplicate loading
              userService.loadLMSUserProfile().then(() => {
                console.log('✅ LMS profile loaded (fallback)');
              }).catch((err) => {
                console.error('LMS Profile Load Error (fallback):', err.message || err);
                // Profile load failed - could be network issue or expired token
                // DON'T automatically trigger login flow here to prevent loops
                // The 401 interceptor or user manual refresh will handle re-auth
                console.warn('⚠️ Profile load failed - user may need to refresh page');
                profileLoaded = false; // Reset flag
              });
            } else {
              console.log('No identity claims, loading user profile from OAuth provider');
              try {
                await oauthService.loadUserProfile();
                const retryProfile = oauthService.getIdentityClaims();
                if (retryProfile && retryProfile['sub']) {
                  profileLoaded = true;
                  userService.loadLMSUserProfile().then(() => {
                    console.log('✅ LMS profile loaded after OAuth profile load');
                  }).catch((err) => {
                    console.error('LMS Profile Load Error after OAuth load:', err.message || err);
                  });
                }
              } catch (error) {
                console.error('Failed to load OAuth user profile:', error);
              }
            }
          }
        }
      } catch (error: any) {
        console.error('OAuth initialization error:', error);

        // Check if error is due to Keycloak being unreachable
        if (error.message?.includes('discovery') || error.status === 0 || error.statusText === 'Unknown Error') {
          console.error('❌ Failed to load OAuth discovery document. Keycloak may be unreachable.');
          console.error(`Keycloak URL: ${authConfig.issuer}`);

          const retryCount = getRetryCount();
          if (retryCount >= MAX_LOGIN_RETRIES) {
            // alert(
            //   'Authentication service unavailable.\n\n' +
            //   'Cannot connect to Keycloak authentication server.\n\n' +
            //   'Please ensure Keycloak is running and accessible.\n\n' +
            //   `Keycloak URL: ${authConfig.issuer}`
            // );
            return;
          }
        }

        // On initialization error, clear tokens but don't retry to prevent loops
        if (!loginInitiated) {
          loginInitiated = true;
          console.log('🔐 OAuth init failed - clearing storage');
          oauthService.logOut(true);
        }
      }
    };
  }
